// VoiceInput — browser-native speech-to-text using Web Speech API.
// No external API or key required. Works in Chrome / Edge / Safari.
// Drop next to any text input as a mic button; transcribed text appends
// to the field. Supports continuous mode for clinical-note dictation.
import React, { useState, useRef, useEffect } from 'react';
import { Mic, MicOff } from 'lucide-react';

export default function VoiceInput({ onTranscript, language = 'en-IN', size = 16, title = 'Dictate', appendMode = true }) {
  const [listening, setListening] = useState(false);
  const [supported, setSupported] = useState(true);
  const recognitionRef = useRef(null);

  useEffect(() => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) { setSupported(false); return; }
    const rec = new SR();
    rec.continuous = true;
    rec.interimResults = true;
    rec.lang = language;
    let finalText = '';
    rec.onresult = (event) => {
      let interim = '';
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const t = event.results[i][0].transcript;
        if (event.results[i].isFinal) finalText += t + ' ';
        else interim = t;
      }
      // Push final text once it's stable; interim ignored to avoid flicker.
      if (finalText.trim()) {
        onTranscript(finalText.trim(), { append: appendMode });
        finalText = '';
      }
    };
    rec.onerror = (e) => { console.warn('[VoiceInput]', e.error); setListening(false); };
    rec.onend = () => setListening(false);
    recognitionRef.current = rec;
    return () => { try { rec.stop(); } catch {} };
  }, [language, appendMode, onTranscript]);

  const toggle = () => {
    if (!recognitionRef.current) return;
    if (listening) { recognitionRef.current.stop(); setListening(false); }
    else { try { recognitionRef.current.start(); setListening(true); } catch {} }
  };

  if (!supported) return null;

  return (
    <button type="button" onClick={toggle} title={title}
      style={{
        padding: 6, borderRadius: 6, border: 'none', cursor: 'pointer',
        background: listening ? '#dc2626' : 'var(--color-bg-inset)',
        color: listening ? 'white' : 'var(--color-text)',
        display: 'inline-flex', alignItems: 'center', gap: 4,
        animation: listening ? 'pulse 1.2s infinite' : 'none',
      }}>
      {listening ? <MicOff size={size}/> : <Mic size={size}/>}
    </button>
  );
}
